import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BookRecommendationCard } from './BookRecommendationCard';
import type { BookRecommendation } from '@/hooks/useBookRecommendations';

export function DirectRecommendationPrompt() {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<BookRecommendation[]>([]);

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe what you\'re looking for');
      return;
    }

    setLoading(true);
    console.log('📝 Sending custom prompt:', prompt);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to get recommendations');
        return;
      }

      const { data, error } = await supabase.functions.invoke('recommend-books', {
        body: { customPrompt: prompt },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      console.log('✅ Got recommendations:', data);
      
      if (data?.recommendations && Array.isArray(data.recommendations)) {
        setRecommendations(data.recommendations);
        toast.success(`Found ${data.recommendations.length} recommendations`);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ Error:', error);
      toast.error('Failed to get recommendations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-primary/20">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
            <div className="flex-1 space-y-3">
              <div>
                <h3 className="font-semibold mb-1">Custom Recommendations</h3>
                <p className="text-sm text-muted-foreground">
                  Describe what you're looking for and get instant recommendations
                </p>
              </div>
              
              <Textarea
                placeholder="Example: I want a mystery novel set in Japan with strong character development..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[100px] resize-none"
                disabled={loading}
              />
              
              <Button
                onClick={handleSubmit}
                disabled={loading || !prompt.trim()}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Finding books...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Get Recommendations
                  </>
                )}
              </Button>
            </div>
          </div>

          {recommendations.length > 0 && (
            <div className="pt-4 border-t">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {recommendations.map((rec, idx) => (
                  <BookRecommendationCard 
                    key={idx} 
                    {...rec}
                    onDismiss={() => {
                      setRecommendations(prev => prev.filter((_, i) => i !== idx));
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
